/**
 * Core Routes Configuration
 * 
 * Base routes utilities that can be used by tenants.
 * Contains core component resolver, layout resolver, and auth guards.
 */

import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Import core layouts
import { BaseLayout, SidebarLayout } from 'src/core/layouts';
import { SuspenseLoader } from 'src/core/components';
import { RootState } from 'src/store';

// Import route builder types
import { LayoutResolver, ComponentResolver } from 'src/core/routing/routeBuilder';

/**
 * Core Layout Enum
 * Defines available core layouts
 */
export enum CoreLayout {
  BaseLayout = 'BaseLayout',
  SidebarLayout = 'SidebarLayout',
}

/**
 * Core Component Enum
 * Defines available core components
 */
export enum CoreComponent {
  // User pages
  UserSignUp = 'UserSignUp',
  UserSignIn = 'UserSignIn',
  // Blog pages
  BlogList = 'BlogList',
  BlogArticle = 'BlogArticle',
  // Status pages
  Status404 = 'Status404',
  Status500 = 'Status500',
  ComingSoon = 'ComingSoon',
  Maintenance = 'Maintenance',
}

/**
 * Mapping from CoreLayout enum to import paths
 */
const CORE_LAYOUT_PATH_MAP: Record<CoreLayout, string> = {
  [CoreLayout.BaseLayout]: 'src/core/layouts/BaseLayout',
  [CoreLayout.SidebarLayout]: 'src/core/layouts/SidebarLayout',
};

/**
 * Mapping from CoreComponent enum to import paths
 */
const CORE_COMPONENT_PATH_MAP: Record<CoreComponent, string> = {
  [CoreComponent.UserSignUp]: 'src/core/pages/user/SignUp',
  [CoreComponent.UserSignIn]: 'src/core/pages/user/SignIn',
  [CoreComponent.BlogList]: 'src/core/pages/blog/List',
  [CoreComponent.BlogArticle]: 'src/core/pages/blog/Article',
  [CoreComponent.Status404]: 'src/core/pages/Status/Status404',
  [CoreComponent.Status500]: 'src/core/pages/Status/Status500',
  [CoreComponent.ComingSoon]: 'src/core/pages/Status/ComingSoon',
  [CoreComponent.Maintenance]: 'src/core/pages/Status/Maintenance',
};

/**
 * Auth Guards
 * These can be customized by tenants if needed
 */
export const createPrivateRoute = (redirectTo: string = '/') => {
  return ({ children }: { children: React.ReactNode }) => {
    const token = useSelector((state: RootState) => state.auth.token);
    return token ? <>{children}</> : <Navigate to={redirectTo} replace />;
  };
};

export const createRedirectIfAuthorized = (dashboardPath: string) => {
  return ({ children }: { children: React.ReactNode }) => {
    const token = useSelector((state: RootState) => state.auth.token);
    return token ? <Navigate to={dashboardPath} replace /> : <>{children}</>;
  };
};

/**
 * Core Layout Resolver
 * Maps layout enum or import paths to actual layout components
 */
export const createCoreLayoutResolver = (): LayoutResolver => {
  return (layoutPath: string | CoreLayout) => {
    // Convert enum to path if needed
    const actualPath = Object.values(CoreLayout).includes(layoutPath as CoreLayout)
      ? CORE_LAYOUT_PATH_MAP[layoutPath as CoreLayout]
      : layoutPath;
    
    const layoutMap: Record<string, React.ComponentType<any>> = {
      'src/core/layouts/BaseLayout': BaseLayout,
      'src/core/layouts/SidebarLayout': SidebarLayout,
    };
    
    const Layout = layoutMap[actualPath];
    if (!Layout) {
      throw new Error(`Layout not found: ${layoutPath}. Available layouts: ${Object.keys(layoutMap).join(', ')}`);
    }
    
    return Layout;
  };
};

/**
 * Core Component Resolver
 * Maps core component enum or import paths to lazy-loaded components
 * This ensures webpack can statically analyze all imports for proper code splitting
 */
export const createCoreComponentResolver = (): ComponentResolver => {
  return (componentPath: string | CoreComponent) => {
    // Convert enum to path if needed
    const actualPath = Object.values(CoreComponent).includes(componentPath as CoreComponent)
      ? CORE_COMPONENT_PATH_MAP[componentPath as CoreComponent]
      : componentPath;
    
    // Map core component paths to lazy imports - webpack can statically analyze these
    const coreComponentMap: Record<string, () => Promise<{ default: React.ComponentType<any> }>> = {
      // Core pages - User
      'src/core/pages/user/SignUp': () => import('src/core/pages/user/SignUp'),
      'src/core/pages/user/SignIn': () => import('src/core/pages/user/SignIn'),
      
      // Core pages - Blog
      'src/core/pages/blog/List': () => import('src/core/pages/blog/List'),
      'src/core/pages/blog/Article': () => import('src/core/pages/blog/Article'),
      
      // Core pages - Status
      'src/core/pages/Status/Status404': () => import('src/core/pages/Status/Status404'),
      'src/core/pages/Status/Status500': () => import('src/core/pages/Status/Status500'),
      'src/core/pages/Status/ComingSoon': () => import('src/core/pages/Status/ComingSoon'),
      'src/core/pages/Status/Maintenance': () => import('src/core/pages/Status/Maintenance'),
    };
    
    const importFn = coreComponentMap[actualPath];
    if (!importFn) {
      throw new Error(`Core component import not found: ${componentPath}. Available core components: ${Object.keys(coreComponentMap).join(', ')}`);
    }
    
    return lazy(importFn);
  };
};

/**
 * Creates a component resolver that extends the core resolver with tenant-specific components
 * 
 * @param tenantComponentMap - Map of tenant-specific component enum values or paths to import functions
 * @returns Combined component resolver
 */
export const createExtendedComponentResolver = (
  tenantComponentMap: Record<string | number, () => Promise<{ default: React.ComponentType<any> }>>
): ComponentResolver => {
  const coreResolver = createCoreComponentResolver();
  
  return (componentPath: string | CoreComponent | number) => {
    // Convert to string for lookup (enums are strings when used as values)
    const pathKey = typeof componentPath === 'number' ? componentPath : String(componentPath);
    
    // First check tenant-specific components (try both enum value and string representation)
    const tenantImportFn = tenantComponentMap[pathKey] || tenantComponentMap[String(pathKey)];
    if (tenantImportFn) {
      return lazy(tenantImportFn);
    }
    
    // Fall back to core components
    return coreResolver(componentPath);
  };
};

